import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, Eye, EyeSlash } from '@phosphor-icons/react';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { resetPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!token) {
      setError('Invalid reset link');
      return;
    }

    setLoading(true);

    try {
      await resetPassword(token, newPassword);
      setSuccess(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
        >
          <div className="bg-zinc-900/50 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <h1 className="text-2xl font-medium font-['Instrument_Serif',serif] mb-4">Invalid Reset Link</h1>
            <p className="text-zinc-400 text-sm mb-6">
              This password reset link is invalid or has expired.
            </p>
            <Link 
              to="/forgot-password" 
              className="text-white hover:underline text-sm"
            >
              Request a new reset link
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={18} />
          <span className="text-sm">Back to login</span>
        </Link>

        <div className="bg-zinc-900/50 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={32} className="text-green-500" weight="fill" />
              </div>
              <h1 className="text-2xl font-medium font-['Instrument_Serif',serif] mb-2">Password Reset!</h1>
              <p className="text-zinc-400 text-sm mb-6">
                Your password has been successfully reset. You can now log in with your new password.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-white text-black hover:bg-zinc-200 transition-all duration-200 rounded-full font-medium py-3"
              >
                Go to Login
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center mx-auto mb-4">
                  <span className="text-black font-bold text-lg">BF</span>
                </div>
                <h1 className="text-2xl font-medium font-['Instrument_Serif',serif] mb-2">Create New Password</h1>
                <p className="text-zinc-400 text-sm">Enter your new password below</p>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6">
                  <p className="text-red-400 text-sm" data-testid="error-message">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">New Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      data-testid="new-password-input"
                      placeholder="••••••••"
                      required
                      minLength={6}
                      className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 pr-12 focus:outline-none focus:ring-1 focus:ring-white/30 focus:border-white/30 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                    >
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  <p className="text-zinc-500 text-xs mt-2">Must be at least 6 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Confirm Password</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    data-testid="confirm-password-input"
                    placeholder="••••••••"
                    required
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 focus:border-white/30 transition-all"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-password-btn"
                  className="w-full bg-white text-black hover:bg-zinc-200 transition-all duration-200 rounded-full font-medium py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
